import * as React from "react"
import styled from "styled-components"
import { SlideOutHeight } from "../atoms/animations/SlideOutHeight"
import { HorizontalSpacer } from "../atoms/spacers"
import H3 from "../atoms/typography/H3"
import PrimaryButton from "../atoms/buttons/PrimaryButton"
import { ArrowDropDownIcon } from "../atoms/icons/ArrowDropDownIcon"
import { ArrowDropUpIcon } from "../atoms/icons/ArrowDropUpIcon"
import MemberProfileOverview from "./MemberProfileOverview"
import { useRemoveUsersFromGroup } from "../../hooks/useRemoveUsersFromGroup"
import { ORGANIZATION_MEMBERS_PATH } from "../../utils/paths"
import { Link } from "react-router-dom"
import SelectRoleDropdown from "../atoms/inputs/SelectRoleDropdown"
import TextButton from "../atoms/buttons/TextButton"
import { useInviteMembersToGroup } from "../../hooks/useinviteMembersToGroup"
import { MediaQuery } from "../../utils/design-helpers"
import { AnimatePresence } from "framer-motion"
import Modal from "./Modals/Modal"
import RemoveMemberFromOrgOrGroupModalContent from "./Modals/RemoveMemberFromOrgOrGroupModalContent"
import { Member } from "../../utils/models"
import { MinusIcon } from "../atoms/icons/MinusIcon"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../utils/sentry"

interface Props {
  member: Member
  groupId: string | number
  orgId: string | number
}

const AddMemberContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const HiddenArrow = styled.div`
  display: none;

  ${MediaQuery.XS} {
    display: block;
  }
`

const ContentContainer = styled.div<{ isOpen?: boolean }>`
  border-radius: ${(props) => props.theme.borders.softRadius};
  padding: ${(props) => props.theme.spacings.medium};
  background-color: ${(props) => (props.isOpen ? props.theme.colors.greyLight : "transparent")};

  ${MediaQuery.MD} {
    :hover {
      background-color: ${(props) => props.theme.colors.greyLight};
    }

    :hover .hiddensection {
      display: block;
    }
  }
`

const IconContainer = styled.div`
  cursor: pointer;

  :hover {
    filter: brightness(60%);
  }
`

const ActionContainer = styled.div`
  padding: ${(props) => props.theme.spacings.large};
`

const RemoveMemberCard: React.FunctionComponent<Props> = ({ member, groupId, orgId }) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [removeMemberModalOpen, setRemoveMemberModalOpen] = React.useState(false)
  const [removedUser, setRemovedUser] = React.useState(false)

  const onCompletedRemoveFromGroup = () => {
    console.log("Removed from group")
    setRemovedUser(true)
    setTimeout(() => {
      setRemovedUser(false)
      setRemoveMemberModalOpen(false)
    }, 2000)
  }
  const onErrorRemoveFromGroup = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error removing user from group")
  }

  const { removeUsersFromGroup } = useRemoveUsersFromGroup({
    onCompleted: onCompletedRemoveFromGroup,
    onError: onErrorRemoveFromGroup,
  })

  const onClickRemoveFromGroup = async () => {
    try {
      await removeUsersFromGroup({
        variables: {
          organizationGroupId: groupId,
          organizationId: orgId,
          users: [member.member.email],
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error removing user from group")
    }
  }

  const [selectedRole, setSelectedRole] = React.useState(member.member.role)
  const onCompletedSendInviteToMember = () => {
    console.log("Sent invite to member")
  }
  const onErrorSendInviteToMember = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error sending invite to member")
  }
  const { inviteMembersToGroup } = useInviteMembersToGroup({
    onCompleted: onCompletedSendInviteToMember,
    onError: onErrorSendInviteToMember,
  })
  const onSendInvite = async () => {
    try {
      await inviteMembersToGroup({
        variables: {
          organizationGroupId: groupId,
          organizationId: orgId,
          users: [{ email: member.member.email, role: selectedRole }],
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error sending invite to member")
    }
  }

  return (
    <ContentContainer isOpen={isOpen}>
      <AddMemberContainer>
        <MemberProfileOverview
          imgSrc={member.member?.src ?? ""}
          name={member.member?.name}
          role={isOpen ? "" : member.member?.role}
          title={isOpen ? "" : member.member?.title}
        />
        <HiddenArrow className="hiddensection">
          <IconContainer onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </IconContainer>
        </HiddenArrow>
      </AddMemberContainer>
      <SlideOutHeight isVisible={isOpen}>
        <ActionContainer>
          <H3>Email: {member.member?.email}</H3>
          <HorizontalSpacer spacing={1} />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <SelectRoleDropdown title="Role:" defaultValue={member.member?.role} onChange={(e) => setSelectedRole(e)} />
            <TextButton title="Save" onClick={onSendInvite} />
          </div>
          <HorizontalSpacer spacing={1} />
          <H3>Title: {member.member?.title}</H3>
          <HorizontalSpacer spacing={1} />
          <Link to={ORGANIZATION_MEMBERS_PATH}>
            <PrimaryButton title="View in members" />
          </Link>
          <HorizontalSpacer spacing={1} />
          <PrimaryButton
            title="Remove from group"
            icon={<MinusIcon />}
            onClick={() => setRemoveMemberModalOpen(true)}
          />
        </ActionContainer>
      </SlideOutHeight>

      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {removeMemberModalOpen && (
          <Modal
            open={removeMemberModalOpen}
            onClose={() => setRemoveMemberModalOpen(!removeMemberModalOpen)}
            closeIcon
          >
            <RemoveMemberFromOrgOrGroupModalContent
              memberName={member.member.name ?? ""}
              onClose={() => setRemoveMemberModalOpen(!removeMemberModalOpen)}
              onClickDelete={onClickRemoveFromGroup}
            />
          </Modal>
        )}
      </AnimatePresence>
    </ContentContainer>
  )
}

export default RemoveMemberCard
